import cloneDeep from 'lodash/cloneDeep';
import toast from 'react-hot-toast';
import {FilterType} from 'enums';
import {IFilter, IUpdate, IUpdateToServer, IUpdateFromServer, IUpdateFile, IUpdateWithFile, IUpdateWithFileServer, IRolloutFromServer, IRollout} from 'interfaces';
// Not using index import for services to avoid circular dependency
import {axiosAPI} from './API';
import { UsersService } from './UsersService';
import { FiltersService } from './FiltersService';

export interface IPostUpdateResponse {
    id: number,
}

class _UpdatesService {
    newUpdateFirstStageStartDate: Date

    public postUpdate(u: IUpdate): Promise<IPostUpdateResponse> {
        const updateServer = this.mapUpdateToServer(u)

        const p = axiosAPI.post<IPostUpdateResponse>(`/updates`, updateServer)
            .then((response) => {
                return response.data;
            })
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public putUpdate(u: IUpdate): Promise<any> {
        const updateServer = this.mapUpdateToServer(u)

        const p = axiosAPI.put(`/updates/${updateServer.id}`, updateServer)
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public getUpdate(id: number): Promise<IUpdate> {
        const u = axiosAPI.get<IUpdateFromServer>(`/updates/${id}`)
            .then((response) => {
                return this.mapUpdateFromServer(response.data);
            })
        u.catch((err) => {
            console.error(err);
        })
        return u;
    }

    public getUpdates(startDate: Date, endDate: Date): Promise<IUpdate[]> {
        const params = new URLSearchParams();
        params.set('startDate', startDate.toISOString());
        params.set('endDate', endDate.toISOString());
        const p = axiosAPI.get<IUpdateWithFileServer[]>('/updates?' + params.toString())
            .then((response) => {
                // Convert dates from strings to real dates
                return response.data.map((u) => {
                    const update = this.mapUpdateFromServer(u);
                    let updateFile: IUpdateFile;
                    if (u.updateFile) {
                        updateFile = {
                            ...u.updateFile,
                            createdAt: new Date(u.updateFile.createdAt),
                        };
                    }

                    return {
                        ...update,
                        updateFile: updateFile,
                    };
                })
            })
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public sendForApproval(id: number): Promise<any> {
        const p = axiosAPI.post('/updates/' + id + '/sendForApproval')
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public approve(id: number): Promise<any> {
        const p = axiosAPI.post('/updates/' + id + '/approve')
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public revertToDraft(id: number): Promise<any> {
        const p = axiosAPI.post('/updates/' + id + '/revertToDraft')
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public cancelUpdate(id: number): Promise<any> {
        const p = axiosAPI.post('/updates/' + id + '/cancel')
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public deleteUpdate(id: number): Promise<any> {
        const p = axiosAPI.delete('/updates/' + id)
            .then(() => {
                toast.success("Update has been deleted.")
            })
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public changeSchedule(u: IUpdate): Promise<any> {
        const updateServer = this.mapUpdateToServer(u)

        const p = axiosAPI.post<IPostUpdateResponse>(`/updates/${u.id}/changeSchedule`, updateServer)
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public confirmStage(updateId: number, stageIndex: number): Promise<any> {
        const p = axiosAPI.post<IPostUpdateResponse>(`/updates/${updateId}/stages/${stageIndex}/confirm`)
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    private mapUpdateToServer(u: IUpdate): IUpdateToServer {
        return {
            ...u,
            rollout: this.mapRolloutToServer(u.rollout),
        }
    }

    private mapUpdateFromServer(u: IUpdateFromServer): IUpdate {
        let filters = []
        if (u.locations.length > 0) {
            const locationFilter: IFilter = {
                id: 1,
                type: FilterType.Locations,
                locations: u.locations
            }
            filters.push(locationFilter)
        }
        if (u.serialNumbers.length > 0) {
            const serialFilter: IFilter = {
                id: 2,
                type: FilterType.Serials,
                serialNumbers: u.serialNumbers
            }
            filters.push(serialFilter)
        }

        const update: IUpdate = {
            ...u,
            approvedAt: u.approvedAt && new Date(u.approvedAt),
            approvedBy: u.approvedBy && UsersService.mapUserFromServer(u.approvedBy),
            createdAt: u.createdAt && new Date(u.createdAt),
            createdBy: u.createdBy && UsersService.mapUserFromServer(u.createdBy),
            modifiedAt: u.modifiedAt && new Date(u.modifiedAt),
            modifiedBy: u.modifiedBy && UsersService.mapUserFromServer(u.modifiedBy),
            rollout: this.mapRolloutFromServer(u.rollout),
            startAt: new Date(u.startAt),
            stopAt: new Date(u.stopAt),
            submittedAt: u.submittedAt && new Date(u.submittedAt),
            submittedBy: u.submittedBy && UsersService.mapUserFromServer(u.submittedBy),
        }

        // Minor validation: the first stage can't require confirmation
        if (update.rollout.stages[0]?.requiresConfirmation) {
            update.rollout.stages[0].requiresConfirmation = false;
        }

        return update;
    }

    public mapRolloutToServer(rollout: IRollout): IRolloutFromServer {
        // Make a copy so we don't mess up the original
        const r: IRollout = cloneDeep(rollout);

        // Minor validation: the first stage can't require confirmation
        if (r?.stages[0]?.requiresConfirmation) {
            r.stages[0].requiresConfirmation = false;
        }

        // Trim dates to minute intervals for consistency. This prevents weirdness if dates are a few seconds off,
        // because in the UI we only show minutes.
        for (const stage of r.stages) {
            if (stage.startTime) stage.startTime.setSeconds(0, 0);
            if (stage.stopTime) stage.stopTime.setSeconds(0, 0);
        }

        return {
            global: {
                filters: r.global.filters.map(FiltersService.mapFilterToServer),
            },
            stages: r.stages.map(stage => ({
                ...stage,
                filters: stage.filters.map(FiltersService.mapFilterToServer),
                startTime: stage.startTime?.toISOString() || '',
                stopTime: stage.stopTime?.toISOString() || '',
            })),
        };
    }

    public mapRolloutFromServer(r: IRolloutFromServer): IRollout {
        const out: IRollout = {
            global: {
                filters: [],
            },
            stages: [],
        }

        // We're being defensive in how we map. Existing updates might be missing
        // properties if the server doesn't migrate them how we expect. Also, Go is dumb about 
        // how it handles slices. If a slice is declared but not initialized, it comes back as null.
        if (r) {
            if (r.global?.filters) {
                out.global.filters = r.global.filters.map(FiltersService.mapFilterFromServer);
            }
            if (r.stages) {
                for (const stage of r.stages) {
                    if (!stage.filters) stage.filters = [];
                    
                    out.stages.push({
                        ...stage,
                        filters: stage.filters.map(FiltersService.mapFilterFromServer),
                        startTime: stage.startTime && new Date(stage.startTime),
                        stopTime: stage.stopTime && new Date(stage.stopTime),
                    })
                }
            }
        }

        return out;
    }
}

export const UpdatesService = new _UpdatesService();